'use client';

import {
  getAppVersion,
  getClientSideUserAgent,
  isClientSide,
  lockBodyScroll,
} from '@vuddy/utils';
import { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import { Overlay } from './Overlay';
import type {
  OverlayComponent,
  OverlayContextType,
  OverlayItem,
} from './types';
import { useWebViewService } from './WebViewServiceContext';

export const OverlayContext = createContext<OverlayContextType>(
  {} as OverlayContextType
);

export const OverlayProvider = ({
  children,
  maxWidth = '100%',
}: {
  children: ReactNode;
  maxWidth?: string;
}) => {
  const [overlays, setOverlays] = useState<OverlayItem[]>([]);
  const releaseScrollLockRef = useRef<ReturnType<typeof lockBodyScroll> | null>(
    null
  );
  const webViewService = useWebViewService();

  const postEnableFullToRefresh = (enabled: boolean) => {
    const userAgent = getClientSideUserAgent();
    const appVersion = getAppVersion(userAgent);
    const isAOSVersion250810 =
      userAgent.includes('AOS') && appVersion === '25.08.10';

    // AOS 25.08.10 버전일 때는 호출하지 않음
    if (isAOSVersion250810) return;

    webViewService.postMessage?.({
      type: 'EnableFullToRefresh',
      data: enabled,
    });
  };

  const openOverlay = ({
    allowDuplicate = false,
    ...overlayComponent
  }: OverlayComponent): number => {
    const id = Date.now();
    setOverlays(prev => {
      const openedINdex = prev.findIndex(openedOverlay => {
        return openedOverlay.Component.component == overlayComponent.component;
      });
      const isSameOverlayOpened = openedINdex > -1;
      if (allowDuplicate || !isSameOverlayOpened) {
        return [...prev, { id, Component: overlayComponent }];
      } else {
        return prev;
      }
    });
    return id;
  };

  const openModal = (
    overlayComponent: Omit<OverlayComponent, 'isBottomSheet'>
  ) => {
    const id = openOverlay({
      ...overlayComponent,
      isBottomSheet: false,
      props: {
        ...overlayComponent.props,
        style: {
          overlay: {
            ...overlayComponent.props?.style?.overlay,
          },
          container: {
            ...overlayComponent.props?.style?.container,
            maxWidth,
          },
        },
      },
    });
    return id;
  };

  const openBottomSheet = (
    overlayComponent: Omit<OverlayComponent, 'isBottomSheet'>
  ) => {
    // bottomSheet가 열려있는 동안 pull to refresh 비활성화
    postEnableFullToRefresh(false);
    // Android에서 간헐적으로 누락되는 케이스 방지를 위해 한 번 더 보냄
    window.setTimeout(() => postEnableFullToRefresh(false), 0);
    window.requestAnimationFrame(() => postEnableFullToRefresh(false));

    openOverlay({
      ...overlayComponent,
      isBottomSheet: true,
      props: {
        ...overlayComponent.props,
        style: {
          overlay: {
            maxWidth: maxWidth,
          },
        },
      },
    });
  };

  const closeOverlay = (id: number, isConfirm: boolean) => {
    setOverlays(prev => {
      const overlayToClose = prev.find(overlay => overlay.id === id);
      const isBottomSheet = overlayToClose?.Component.isBottomSheet;

      if (overlayToClose?.Component.props?.onCloseAction) {
        overlayToClose.Component.props.onCloseAction(isConfirm);
      }

      const newOverlays = prev.filter(overlay => overlay.id !== id);

      // 마지막 bottomSheet가 닫힐 때 pull to refresh 활성화
      // AOS 25.08.10 버전일 때는 호출하지 않음
      if (
        isBottomSheet &&
        !newOverlays.some(overlay => overlay.Component.isBottomSheet)
      ) {
        postEnableFullToRefresh(true);
        window.setTimeout(() => postEnableFullToRefresh(true), 0);
        window.requestAnimationFrame(() => postEnableFullToRefresh(true));
      }

      return newOverlays;
    });
  };

  useEffect(() => {
    if (overlays.length > 0) {
      releaseScrollLockRef.current ??= lockBodyScroll();
      return;
    }

    releaseScrollLockRef.current?.();
    releaseScrollLockRef.current = null;
  }, [overlays.length]);

  useEffect(() => {
    return () => {
      releaseScrollLockRef.current?.();
      releaseScrollLockRef.current = null;
    };
  }, []);

  return (
    <OverlayContext.Provider
      value={{ openBottomSheet, openModal, closeOverlay, overlays }}>
      {children}
      {isClientSide &&
        overlays.map(({ id, Component }, index) => (
          <Overlay
            key={id}
            id={id}
            Component={Component}
            isActive={overlays.length - 1 === index}
          />
        ))}
    </OverlayContext.Provider>
  );
};
